/* The Machine wise page's API client and the shapes it returns.
 *
 * Kept beside api.ts rather than in it so the page can be built and wired
 * without touching the shared client. */

import { request } from './api'

export type Shift = 'A' | 'B' | 'C'
/** What a machine is measured against: the sheet's own standard, or — for
 *  the yarn stages — the count's SITRA norm times the frame's spindles. */
export type Basis = 'settings' | 'sitra'
/** 'ring frames and autoconers' — the stages on the norm, as a phrase. */
export const stageNoun = (stages: string[]) =>
  stages.map((s) => ({ 'Ring Frame': 'ring frames', 'Auto Coner': 'autoconers' }[s] ?? s.toLowerCase()))
    .join(' and ')
export const SHIFTS: Shift[] = ['A', 'B', 'C']

export type Pattern =
  | 'On standard' | 'Standard suspect' | 'Shift-linked' | 'Erratic'
  | 'Chronic low' | 'Declining' | 'Marginal'

/** One machine's rated shifts on one day. A/B/C are that shift's own
 *  efficiency, null when the shift has no rated row. */
export interface MachineDay {
  date: string
  eff: number
  actual: number
  standard: number
  /** The count run that day, carried forward over days the sheet left
   *  blank; '' when the machine has no count typed yet. */
  count: string
  /** The standard in force that day, kg per shift. */
  std: number | null
  /** Each shift's own kilograms; null when the shift has no rated row. */
  kg: Record<Shift, number | null>
  /** Stoppage minutes booked on each shift, and the shift's length. */
  stop_min: Record<Shift, number>
  shift_min: number | null
  /** Each shift's record: the register's stoppages on it and who had it. */
  shifts: Record<Shift, { stops: { reason: string; min: number }[]; supervisor: string | null }>
  A: number | null
  B: number | null
  C: number | null
}

/** A best or worst day: the shift that made it so, and who had it. */
export interface MachineExtreme {
  date: string
  eff: number
  shift: Shift | null
  supervisor: string | null
}

export interface MachineRow {
  code: string
  unit: number
  stage: string
  /** 'RF 5' */
  name: string
  /** 'U1 · RF 5 · 30 KW' — or 'U1 · RF 5 · 20 KW → 30 KW' when it changed. */
  full_name: string
  /** What it was making on its latest rated shift, '' when not typed; the
   *  sequence '20 KW → 30 KW' when the count changed during the month. */
  count: string
  /** The counts run, in order, one entry per run; more than one means the
   *  count changed. */
  counts: string[]
  /** A segment: one machine's days on one count alone, read as a machine of
   *  its own with `code` = machine code + '|' + count. `parent` is the
   *  machine's code and `range` the days it ran ('5–18 Sep'). */
  is_seg: boolean
  parent: string | null
  range: string | null
  /** Standard kg per shift on its latest rated shift. */
  std: number | null
  /** Rated days. */
  n: number
  /** Average of the day efficiencies, and their spread (population SD). */
  mean: number
  sd: number
  actual: number
  standard: number
  /** Kilograms short of standard, summed over the days that fell short. */
  lost: number
  /** Each shift's average efficiency over the days it ran. */
  shifts: Record<Shift, number | null>
  /** Best shift minus worst, when all three ran; 0 otherwise. */
  spread: number
  slope: number
  first: number
  last: number
  /** Days under the benchmark. */
  below: number
  best: MachineExtreme
  worst: MachineExtreme
  pattern: Pattern
  /** Its stage's own efficiency, within its unit. */
  stage_mean: number | null
  days: MachineDay[]
  /** Stoppage minutes booked against this machine, by folded reason. */
  stop_min: number
  stops: { reason: string; min: number }[]
  /** The same, on its below-benchmark days only. */
  weak_stop_min: number
  weak_top: string | null
  /** Shifts on the sheet with no output, and shifts left out of efficiency. */
  no_output: number
  unrated: number
}

/** What one machine is measured against, from its latest shift with
 *  output: the settings and the standard they give, and on the yarn stages
 *  the count's SITRA norm and the standard that gives. */
export interface MachineStandard {
  code: string
  unit: number
  stage: string
  name: string
  count: string
  date: string
  shift: Shift
  hours: number
  /** Working positions on the row; spindles the frame is built with. */
  positions: number | null
  installed: number | null
  speed_mpm: number | null
  spindle_rpm: number | null
  tm: number | null
  tpi: number | null
  /** The standard cell was typed over the sheet's formula and could not be believed. */
  typed: boolean
  settings_kg: number | null
  /** Settings standard over working positions, g/spindle/shift (yarn stages only). */
  settings_gps: number | null
  on_norm: boolean
  norm_gps: number | null
  norm_max_gps: number | null
  norm_source: string | null
  sitra_kg: number | null
  in_use: Basis
}

export interface MachineSummary {
  empty: boolean
  month: string
  months: string[]
  benchmark: number
  erratic: number
  basis: Basis
  /** The stages read against the SITRA norm; empty on the settings basis.
   *  Every other stage is on its settings whatever the basis. */
  norm_stages: string[]
  dates: string[]
  scope: { unit: number | null; stage: string | null }
  units: number[]
  stages: string[]
  /** The whole plant for the month, whatever the filters say. */
  plant: { machines: number; stages: number; on_file: number }
  /** The unit's last yarn stage — autoconers, else ring frames. */
  yarn: {
    stage: string; machines: number
    actual: number; standard: number; efficiency: number | null; lost: number
  }
  totals: {
    machines: number
    actual: number; standard: number; efficiency: number | null; lost: number
    machine_days: number; below: number; flagged: number; stop_min: number
    /** Machine-shifts typed in scope, and why some are not rated. */
    rows: number; rated: number; no_output: number
    no_standard: number; implausible: number; over: number
    output: number; unrated_kg: number
    untracked: { name: string; why: 'days' | 'standard' }[]
  }
  min_days: number
  over_standard: number
  /** False when the month has no stoppage register at all (August). */
  register: boolean
  machines: MachineRow[]
  /** Every machine typed in scope, with what its efficiency is divided by. */
  standards: MachineStandard[]
  /** The machines that changed count, read once per count they ran — two
   *  rated days on it at least. The design computes these as `split` and
   *  does not draw them; they are here for the day the register does. */
  segments: MachineRow[]
  /** Machine codes, worst first. */
  findings: string[]
  stage_rows: {
    stage: string; unit: number; machines: number
    efficiency: number | null; lost: number
    weakest: { code: string; name: string; mean: number }
  }[]
  stoppages: { total: number; rows: { reason: string; min: number; share: number }[] }
  /** The register by unit, day and reason, over every machine on file. */
  stops_by_day: {
    date: string; unit: number; reason: string; min: number
    /** The unit's machine time that day (machine-shifts that ran × shift
     *  length), and the reason's share of it. */
    machine_min: number; pct: number | null
  }[]
  /** One value per date in `dates`; null where the stage has nothing rated. */
  stage_lines: { stage: string; values: (number | null)[] }[]
  shift_lines: Record<Shift, (number | null)[]>
  shift_rows: ({
    stage: string; unit: number; spread: number; weakest: Shift | null
  } & Record<Shift, number | null>)[]
  shift_all: Record<Shift, number | null>
  /** Machine codes, widest gap between shifts first. */
  shift_gaps: string[]
}

export const machines = {
  summary: (q: { month?: string; unit?: number | null; stage?: string | null; basis?: Basis } = {}) => {
    const p = new URLSearchParams()
    if (q.month) p.set('month', q.month)
    if (q.unit) p.set('unit', String(q.unit))
    if (q.stage) p.set('stage', q.stage)
    if (q.basis && q.basis !== 'settings') p.set('basis', q.basis)
    const qs = p.toString()
    return request<MachineSummary>(`/machines/summary${qs ? `?${qs}` : ''}`)
  },
}
